import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useInView, useCountUp } from "./Sections";
import MovingBg, { BG } from "../MovingBg";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";

function Stat({ label, value, suffix, started, testid }) {
  const v = useCountUp(value, started);
  const display = value >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v.toLocaleString("en-IN");
  return (
    <div data-testid={testid} className="text-center">
      <p className="font-heading text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
        {display}<span className="text-saffron">{suffix}</span>
      </p>
      <p className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</p>
    </div>
  );
}

export function StatsSection({ stats }) {
  const [ref, inView] = useInView();
  const s = stats || {};
  return (
    <section ref={ref} className="border-y border-slate-200 tri-wash relative overflow-hidden py-16">
      <MovingBg src={BG.port} anim="kenburns-a" opacity={0.15} />
      <div className="relative mx-auto grid max-w-7xl grid-cols-2 gap-10 px-4 sm:px-6 lg:grid-cols-5">
        <Stat label="Pincodes Covered" value={s.pincodes_covered || 29000} suffix="+" started={inView} testid="stat-pincodes" />
        <Stat label="Countries Served" value={s.countries_served || 220} suffix="+" started={inView} testid="stat-countries" />
        <Stat label="Shipments Delivered" value={s.shipments_delivered || 10000000} suffix="+" started={inView} testid="stat-shipments" />
        <Stat label="Happy Customers" value={s.customers || 85000} suffix="+" started={inView} testid="stat-customers" />
        <Stat label="Courier Partners" value={s.courier_partners || 17} suffix="+" started={inView} testid="stat-partners" />
      </div>
    </section>
  );
}

export function TestimonialsSection({ testimonials }) {
  const items = testimonials?.length ? testimonials : [];
  if (!items.length) return null;
  return (
    <section className="tri-wash relative overflow-hidden py-20">
      <MovingBg src={BG.bike} anim="kenburns-b" opacity={0.11} />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-saffron">Testimonials</p>
      <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">Trusted by shippers across India</h2>
      <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {items.map((t, i) => (
          <figure key={i} data-testid={`testimonial-card-${i}`} className="rounded-2xl border border-slate-200 bg-white p-6">
            <Quote className="h-6 w-6 text-saffron/40" />
            <blockquote className="mt-3 text-sm leading-relaxed text-slate-700">{t.quote}</blockquote>
            <figcaption className="mt-4 border-t border-slate-100 pt-3">
              <p className="font-heading text-sm font-bold text-navy">{t.name}</p>
              <p className="text-xs text-slate-500">{t.company}</p>
            </figcaption>
          </figure>
        ))}
      </div>
      </div>
    </section>
  );
}

export function FaqSection({ faqs }) {
  const items = faqs?.length ? faqs : [];
  if (!items.length) return null;
  return (
    <section className="tri-wash-alt relative overflow-hidden py-20">
      <MovingBg src={BG.ship} anim="kenburns-a" opacity={0.10} />
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6">
        <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-saffron">FAQ</p>
        <h2 className="mt-2 text-center font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">Questions, answered</h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
          {items.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-heading font-semibold text-navy">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function CtaSection() {
  return (
    <section id="franchise" className="relative scroll-mt-24 overflow-hidden bg-navy py-24">
      <div className="tricolor-beam absolute inset-x-0 top-0 h-1" />
      <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-saffron/20 blur-3xl" />
      <div className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-emerald-600/20 blur-3xl" />
      <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">Ready to Ship?</h2>
        <p className="mx-auto mt-4 max-w-xl text-base text-slate-400">
          Start shipping in minutes — or partner with INDIAFAST and earn 10% commission + ₹10/kg as a franchise, 30% + ₹30/lead as an agent.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/register" data-testid="cta-start-shipping-btn"
                className="group flex items-center gap-2 rounded-full bg-saffron px-7 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-colors hover:bg-saffron-hover">
            Start Shipping <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/partner-with-us" data-testid="cta-talk-btn"
                className="rounded-full border border-white/20 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10">
            Partner With Us
          </Link>
        </div>
      </div>
    </section>
  );
}

const COURIER_DOMAINS = {
  delhivery: "delhivery.com",
  "blue dart": "bluedart.com",
  xpressbees: "xpressbees.com",
  ekart: "ekartlogistics.com",
  dtdc: "dtdc.in",
  dhl: "dhl.com",
  fedex: "fedex.com",
  aramex: "aramex.com",
  shadowfax: "shadowfax.in",
  "ecom express": "ecomexpress.in",
};

function PartnerLogo({ name, testid }) {
  const initials = (name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span data-testid={testid}
          className="flex items-center gap-3 whitespace-nowrap rounded-2xl border border-slate-200 bg-white px-5 py-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-saffron/40 hover:shadow-lg">
      <span className="grid h-9 w-9 place-items-center rounded-lg bg-navy text-xs font-bold text-white">
        {initials}
      </span>
      <span className="font-heading text-lg font-extrabold tracking-tight text-navy">{name}</span>
    </span>
  );
}

export function PartnersMarquee({ partners }) {
  const items = partners?.length ? partners : [];
  if (!items.length) return null;
  const doubled = [...items, ...items];
  return (
    <section className="overflow-hidden border-y border-slate-200 bg-white py-12">
      <p className="mb-7 text-center text-xs font-bold uppercase tracking-[0.25em] text-slate-400">
        Trusted Courier Network — 17+ Partners
      </p>
      <div className="flex w-max animate-marquee gap-6 px-6">
        {doubled.map((p, i) => (
          <PartnerLogo key={i} name={p.name} testid={`partner-logo-${i}`} />
        ))}
      </div>
    </section>
  );
}
